import Table from '@shared/components/Table';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadPersonFormReturnContext } from '@utils/personFormReturnStorage';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import ActionButtons from '@shared/components/ActionButtons';
import { compensationsService } from '../services/compensations';
import CompensationsForm from './CompensationsForm';
import DeleteModal from '@shared/components/DeleteModal';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';


export default function Compensations({ person: personProp }) {
    const navigate = useNavigate();
    const location = useLocation();
    /** Person comes from navigate(..., { state: { person } }) — see PersonContractHeading; route has no prop. */
    const person = personProp ?? location.state?.person;
    /** Same array as getMainContractsByPersonId in PersonContractHeading (passed on navigate). */
    const mainContracts =
        location.state?.mainContracts ??
        person?.mainContracts ??
        loadPersonFormReturnContext()?.mainContracts;
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [firstData, setFirstData] = useState([]);
    const [secondData, setSecondData] = useState([]);
    const [modalState, setModalState] = useState({ visible: false, type: null, variant: 'employee' });
    const [selectedEmployeeRecord, setSelectedEmployeeRecord] = useState(null);
    const [selectedEmployerRecord, setSelectedEmployerRecord] = useState(null);

    /** Contract context from person form navigation (PersonContractHeading). Used for GET/POST compensations APIs. */
    const contractId = useMemo(() => {
        const ctx = loadPersonFormReturnContext();
        const raw =
            person?.selectedContractId ??
            person?.selectedMainContract ??
            ctx?.selectedContractId ??
            ctx?.selectedMainContract;
        if (raw === null || raw === undefined || raw === '') return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    }, [person?.selectedContractId, person?.selectedMainContract]);

    const loadCompensationTables = useCallback(async () => {
        if (contractId == null) {
            setFirstData([]);
            setSecondData([]);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [empRows, emplrRows] = await Promise.all([
                compensationsService.getEmployeeCompensations(contractId),
                compensationsService.getEmployerCompensations(contractId),
            ]);
            setFirstData(empRows);
            setSecondData(emplrRows);
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || t('common.errorMessage'));
            setFirstData([]);
            setSecondData([]);
        } finally {
            setLoading(false);
        }
    }, [contractId, t]);

    useEffect(() => {
        loadCompensationTables();
    }, [loadCompensationTables]);

    const handleExit = () => {
        const routeFromState = location.state?.returnPath;
        const routeFromContext = loadPersonFormReturnContext()?.returnPath;
        navigate(routeFromState || routeFromContext || '/personnel/employment-contracts/register', {
            state: { restorePersonForm: true },
        });
    };

    const firstColumns = [
        { key: 'code', label: t('persons.compensations.code') },
        { key: 'reason', label: t('persons.compensations.reason') },
        { key: 'compensationType', label: t('persons.compensations.typeOfCompensation') },
        { key: 'amount', label: t('persons.compensations.count') }
    ];
    const secondColumns = [
        { key: 'code', label: t('persons.compensations.code') },
        { key: 'reason', label: t('persons.compensations.reason') },
        { key: 'compensationType', label: t('persons.compensations.typeOfCompensation') },
        { key: 'amount', label: t('persons.compensations.count') },
        { key: 'year', label: t('persons.compensations.forYear') },
        { key: 'totalAmount', label: t('persons.compensations.sumToBeReceived') },
    ];

    const openAddModal = (variant) => setModalState({ visible: true, type: 'add', variant });

    const openDeleteModal = (variant) => setModalState({ visible: true, type: 'delete', variant });

    const closeModal = () => setModalState({ visible: false, type: null, variant: 'employee' });

    const handleDelete = (variant) => {
        openDeleteModal(variant);
    };

    const handleConfirmDelete = async () => {
        try {
            if (modalState.variant === 'employee') {
                if (!selectedEmployeeRecord?.id) return;
                await compensationsService.deleteEmployeeCompensation(selectedEmployeeRecord.id);
                setSelectedEmployeeRecord(null);
            } else {
                if (!selectedEmployerRecord?.id) return;
                await compensationsService.deleteEmployerCompensation(selectedEmployerRecord.id);
                setSelectedEmployerRecord(null);
            }
            closeModal();
            await loadCompensationTables();
            notifySuccess(t('common.deletedSuccessfully'));
        } catch (e) {
            console.error('Delete compensation record:', e);
            notifyError(e?.response?.data?.message || e?.message || t('common.errorMessage'));
        }
    };

    const employeeActionButtons = (
        <ActionButtons
            buttons={[
                {
                    ...COMMON_BUTTONS.ADD,
                    onClick: () => openAddModal('employee'),
                    showWhen: 'always',
                },
                {
                    ...COMMON_BUTTONS.DELETE,
                    onClick: () => handleDelete('employee'),
                    showWhen: 'whenSelected',
                },
            ]}
            selectedRecord={selectedEmployeeRecord}
        />
    );

    const employerActionButtons = (
        <ActionButtons
            buttons={[
                {
                    ...COMMON_BUTTONS.ADD,
                    onClick: () => openAddModal('employer'),
                    showWhen: 'always',
                },
                {
                    ...COMMON_BUTTONS.DELETE,
                    onClick: () => handleDelete('employer'),
                    showWhen: 'whenSelected',
                },
            ]}
            selectedRecord={selectedEmployerRecord}
        />
    );

    return (
        <div className="flex-column width-100">
            <h4 className='text-center'>{t('persons.compensations.title', { name: person?.fullName })}</h4>
            {Array.isArray(mainContracts) &&
                mainContracts.map((c) => (
                    <h4 key={String(c.contractId ?? c.contractNo ?? c.name)} className="text-center">
                        {c.name ?? ''}
                    </h4>
                ))}
            <Table
                title={t('persons.compensations.obligations')}
                columns={firstColumns}
                data={firstData}
                loading={loading}
                error={error}
                showPageSizeSelector={false}
                showCustomSearch={true}
                showPagination={false}
                actionButtons={employeeActionButtons}
                showPreviousPage={false}
                selectedData={selectedEmployeeRecord?.id}
                handleCheckboxChange={(index) => setSelectedEmployeeRecord(firstData[index])}
            />
            <Table
                title={t('persons.compensations.compensations')}
                columns={secondColumns}
                data={secondData}
                loading={loading}
                error={error}
                showPageSizeSelector={false}
                showCustomSearch={true}
                showPagination={false}
                actionButtons={employerActionButtons}
                showPreviousPage={false}
                selectedData={selectedEmployerRecord?.id}
                handleCheckboxChange={(index) => setSelectedEmployerRecord(secondData[index])}
            />
            {modalState.visible && modalState.type === 'add' && (
                <CompensationsForm
                    variant={modalState.variant}
                    contractId={contractId}
                    onClose={closeModal}
                    onSuccess={loadCompensationTables}
                />
            )}
            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={closeModal}
                    onConfirm={handleConfirmDelete}
                    name={
                        modalState.variant === 'employee'
                            ? (selectedEmployeeRecord?.reason ?? selectedEmployeeRecord?.code ?? '')
                            : (selectedEmployerRecord?.reason ?? selectedEmployerRecord?.code ?? '')
                    }
                    entityType="record"
                />
            )}
            <div className="position-center">
                <button className="form-btn btn-red mt-2 width-10" onClick={handleExit}>{t('common.exit')}</button>
            </div>
        </div>
    )
}